"use client";

import React, { useEffect, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from "@/components/ui/carousel";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { Gamepad2, Zap, BookOpen } from "lucide-react";
const wp_query_params = {
  per_page: 1
};
const wp_query = new WP_Query(wp_query_params);
function FeaturedProductConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div className="h-full w-full flex items-center justify-center p-6 md:p-12 lg:p-24">
        <Skeleton className="w-full max-w-6xl h-[60vh] rounded-2xl border border-border/20 shadow-2xl" />
      </div>;
  }
  if (error || products.length === 0) {
    return <div className="h-full w-full flex items-center justify-center p-6 md:p-12 lg:p-24">
        <div className="w-full max-w-6xl h-[60vh] rounded-2xl border border-border/20 shadow-2xl bg-card/40 backdrop-blur-md flex items-center justify-center">
          <p className="font-serif text-muted-foreground uppercase tracking-widest">Product Data Unavailable</p>
        </div>
      </div>;
  }
  const product = products[0];
  return <div className="h-full w-full flex items-center justify-center p-6 md:p-12 lg:p-24">
      <div className="max-w-6xl w-full grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 border border-border/30 p-8 lg:p-12 rounded-2xl shadow-2xl bg-card/60 backdrop-blur-xl relative overflow-hidden">
        {}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-primary/5 blur-[120px] rounded-full pointer-events-none" />
        
        <div className="relative aspect-square lg:aspect-auto lg:h-full rounded-xl overflow-hidden border border-border/40 bg-muted/50 flex items-center justify-center shadow-lg group">
          {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-105" loading="lazy" /> : <Gamepad2 className="w-24 h-24 text-muted-foreground/50" />}
          <Badge className="absolute top-6 left-6 font-serif uppercase tracking-wider bg-destructive text-destructive-foreground border-none shadow-md px-4 py-1.5 text-sm">
            <Zap className="w-4 h-4 mr-2" />
            Featured Game
          </Badge>
        </div>
        
        <div className="flex flex-col justify-center space-y-8 relative z-10">
          <div className="space-y-4">
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-black uppercase text-foreground leading-none tracking-tight drop-shadow-md">
              {product.title}
            </h2>
            <div className="font-serif text-3xl text-primary font-bold drop-shadow-[0_0_8px_rgba(0,199,255,0.4)]">
              ${product.price?.toFixed(2)}
            </div>
          </div>
          
          {product.short_description && <div className="font-sans text-lg text-foreground/80 prose prose-invert max-w-none leading-relaxed" dangerouslySetInnerHTML={{
          __html: product.short_description
        }} />}
          
          <div className="pt-4 flex flex-col sm:flex-row gap-4">
            <AddToCartButton productId={product.id} className="w-full" buttonClassName="w-full sm:w-auto bg-primary text-primary-foreground font-serif uppercase tracking-widest shadow-lg hover:bg-primary/90 hover:shadow-[0_0_20px_rgba(0,199,255,0.6)] transition-all duration-300 h-14 px-8 text-lg" text="Add to Cart" showQuantity={false} />
            <Button variant="outline" className="w-full sm:w-auto font-serif uppercase tracking-widest border-border/50 text-foreground hover:bg-accent hover:text-accent-foreground hover:border-accent transition-all duration-300 h-14 px-8 text-lg" asChild>
              <Link to={product.link}>View Details</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>;
}
export default function PremiumArcadeHeroCarousel() {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
    const interval = setInterval(() => {
      api.scrollNext();
    }, 6000);
    return () => clearInterval(interval);
  }, [api]);
  return <section id="premiumarcadeherocarousel" className="relative w-full min-h-screen h-screen bg-background text-foreground overflow-hidden flex flex-col">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-muted/20 via-background to-background pointer-events-none" />
      <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1661517/img/noise_texture.png')] opacity-[0.03] mix-blend-overlay pointer-events-none" />

      <Carousel setApi={setApi} opts={{
      loop: true,
      align: "start"
    }} className="w-full h-full flex-1">
        <CarouselContent className="h-full m-0">
          
          {}
          <CarouselItem data-index={0} className="h-full p-0 basis-full">
            <div className="h-full w-full flex items-center justify-center p-6 md:p-12 lg:p-24">
              <div className="max-w-6xl w-full grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 border border-border/30 p-8 lg:p-12 rounded-2xl shadow-2xl bg-card/60 backdrop-blur-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-accent/10 blur-[80px] rounded-full pointer-events-none" />
                
                <div className="flex flex-col justify-center space-y-8 relative z-10">
                  <div className="space-y-4">
                    <Badge className="w-fit font-serif uppercase tracking-widest bg-accent text-accent-foreground border-none px-3 py-1">
                      Analog Arcade
                    </Badge>
                    <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-black uppercase text-foreground leading-[0.9] tracking-tighter drop-shadow-lg">
                      Defying <br className="hidden lg:block" />
                      <span className="text-primary drop-shadow-[0_0_10px_rgba(0,199,255,0.5)]">Boring</span> <br className="hidden lg:block" />
                      Since 1980
                    </h1>
                  </div>
                  <p className="font-sans text-xl text-foreground/80 leading-relaxed max-w-md">
                    Original tabletop games with arcade soul, premium components, immersive worlds, and endless replayability.
                  </p>
                  <Button className="w-fit font-serif uppercase tracking-widest bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 hover:shadow-[0_0_20px_rgba(0,199,255,0.6)] transition-all duration-300 h-14 px-10 text-lg" asChild>
                    <Link to="/ShopPage">Enter The Arcade</Link>
                  </Button>
                </div>
                
                <div className="relative aspect-square lg:aspect-auto lg:h-full rounded-xl overflow-hidden border border-border/40 shadow-inner group">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1661517/img/hero_brand_showcase.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1661517/img/hero_brand_showcase.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1661517/img/hero_brand_showcase.png-wvc-sizes" alt="Analog Arcade Premium Components" className="object-cover w-full h-full transition-transform duration-1000 group-hover:scale-110" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                </div>
              </div>
            </div>
          </CarouselItem>

          {}
          <CarouselItem data-index={1} className="h-full p-0 basis-full">
            <EcommerceProductsProvider wp_query={{
            per_page: 1
          }}>
              <FeaturedProductConsumer />
            </EcommerceProductsProvider>
          </CarouselItem>

          {}
          <CarouselItem data-index={2} className="h-full p-0 basis-full">
            <div className="h-full w-full flex items-center justify-center p-6 md:p-12 lg:p-24">
              <div className="max-w-6xl w-full grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 border border-border/30 p-8 lg:p-12 rounded-2xl shadow-2xl bg-card/60 backdrop-blur-xl relative overflow-hidden">
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary/10 blur-[100px] rounded-full pointer-events-none" />
                
                <div className="flex flex-col justify-center space-y-8 relative z-10 order-2 lg:order-1">
                  <div className="space-y-4">
                    <Badge className="w-fit font-serif uppercase tracking-widest bg-secondary text-secondary-foreground border-none px-3 py-1 shadow-sm">
                      <BookOpen className="w-4 h-4 mr-2 inline-block" />
                      Latest Transmission
                    </Badge>
                    <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-black uppercase text-foreground leading-none tracking-tight drop-shadow-md">
                      Designing the <span className="text-primary">Neon Grid</span>
                    </h2>
                  </div>
                  <p className="font-sans text-lg text-foreground/80 leading-relaxed">
                    Dive deep into our creative process as we explore the mechanics and aesthetics that bring the neon-drenched world of Dice Command to life on your tabletop. Discover the secrets behind the premium components.
                  </p>
                  <Button variant="outline" className="w-fit font-serif uppercase tracking-widest border-primary text-primary hover:bg-primary hover:text-primary-foreground shadow-[0_0_10px_rgba(0,199,255,0.2)] hover:shadow-[0_0_20px_rgba(0,199,255,0.6)] transition-all duration-300 h-14 px-10 text-lg" asChild>
                    <Link to="/TheArcadePage">Read Full Log</Link>
                  </Button>
                </div>
                
                <div className="relative aspect-square lg:aspect-auto lg:h-full rounded-xl overflow-hidden border border-border/40 shadow-lg order-1 lg:order-2 group">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1661517/img/blog_feature_neon_grid.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1661517/img/blog_feature_neon_grid.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1661517/img/blog_feature_neon_grid.png-wvc-sizes" alt="Behind the scenes of Dice Command" className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-105 grayscale-[20%] group-hover:grayscale-0" loading="lazy" />
                  <div className="absolute inset-0 border-4 border-primary/20 mix-blend-overlay rounded-xl pointer-events-none" />
                </div>
              </div>
            </div>
          </CarouselItem>

        </CarouselContent>

        {}
        <div className="hidden md:block">
          <CarouselPrevious className="left-8 lg:left-12 w-14 h-14 bg-card/50 backdrop-blur-md border-border/50 text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all shadow-lg" />
          <CarouselNext className="right-8 lg:right-12 w-14 h-14 bg-card/50 backdrop-blur-md border-border/50 text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all shadow-lg" />
        </div>
      </Carousel>

      {}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3 z-20">
        {Array.from({
        length: count
      }).map((_, i) => <Button key={i} data-index={i} variant="ghost" size="icon" className={`w-12 h-1.5 min-w-0 min-h-0 p-0 rounded-full transition-all duration-300 ${current === i + 1 ? "bg-primary shadow-[0_0_10px_rgba(0,199,255,0.8)]" : "bg-border/30 hover:bg-border/60"}`} onClick={() => api?.scrollTo(i)} aria-label={`Go to slide ${i + 1}`} />)}
      </div>
    </section>;
}